import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
    Layout,
    Menu,
    Breadcrumb,
    Button,
    MenuProps,
    Flex,
    theme,
} from '@app/ui';
import { useTranslation } from 'react-i18next';
import connect from '@app/utils/connect';
import AvatarMenu from '../avatar-menu/AvatarMenu'; // Keep AvatarMenu as it is
import userActions from '@app/actions/user';
import ReportDownloading from '@app/reports/ReportDownloading';
import CollectionsSnack from '@app/collections/components/CollectionsSnack';
import CampaignSnack from '@app/allSocialMediaPosts/components/CampaignSnack';
import ContainerContext from '../ContainerContext';
import { Client, User } from '@app/types/api';
import { HomeTabContext } from '../../home/HomeTabContext';
import {
    AUTOMATED_CAMPAIGNS,
    AUTOMATED_COLLECTIONS,
    EDGE_RIGHTSHOLDER_ATTRIBUTION,
} from '../../constants/config';
import GdprConsent from '../../gdpr-consent/GdprConsent';
import { getNestedMenuOptions, getBreadcrumbMap } from '../menu/getMenuOptions';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import relo_edge_full from '../../images/relo_edge_full.svg';
import relo_edge_circle from '../../images/relo_edge_circle.svg';
import useDebounce from '../hooks/useDebounce';
import useHasScrolled from './useHasScrolled';
import RhAttributionToggle from './RhAttributionToggle';
import { useRightsholderAttribution } from '../hooks/useRightsholderAttribution';
import ResourceCenter from '../resource-center/ResourceCenter';
import './AppContainer.scss';

const { Header, Content, Sider } = Layout;

declare let __VERSION__: string;

type MenuItem = Required<MenuProps>['items'][number];

const uniformPath = (path: string) =>
    !path?.endsWith('/') ? path + '/' : path;

type AppContainerProps = {
    children: React.ReactNode;
    client: Client;
    user: User;
    getFilters: () => void;
};

const AppContainer = ({
    children,
    client,
    user,
    getFilters,
}: AppContainerProps) => {
    const { t } = useTranslation();
    const { isLoggedIn, clients = [] } = user;

    const {
        token: { colorBgContainer, colorBgLayout },
    } = theme.useToken();

    const navigate = useNavigate(); // Hook to handle navigation

    const [collapsed, setCollapsed] = useState(false);
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    const deboucedCollapsed = useDebounce(collapsed, 100);

    const containerRef = useRef(null);

    const toggleMenu = () => {
        setCollapsed(!collapsed);
    };

    const menuOptions = React.useMemo(
        () => getNestedMenuOptions(client, true, t),
        [client, t],
    );

    const hasScrolled = useHasScrolled();

    const valuationStatusId = client?.valuationStatusId;
    const showOnboardingOnly = valuationStatusId == '1';
    const showAutomatedCampaigns =
        client.permissions?.includes(AUTOMATED_CAMPAIGNS) || false;
    const showAutomatedCollections =
        client.permissions?.includes(AUTOMATED_COLLECTIONS) || false;
    const showRhAttribution = client.permissions?.includes(
        EDGE_RIGHTSHOLDER_ATTRIBUTION,
    );
    const { rightsholderAttribution } = useRightsholderAttribution({ client });
    const agreed = user?.agreementsAccepted;
    const headerImgUrl = client?.headerImageUrl;
    const name = client?.name;

    const { pathname } = useLocation();

    const fullPathname = pathname.endsWith('/') ? pathname : pathname + '/';

    // Check if we're on the home page
    const isOnHomePage = React.useMemo(() => {
        // Home page route pattern: /c/:clientId or /c/:clientId/
        const homePagePattern = /^\/c\/[^/]+\/?$/;
        return homePagePattern.test(pathname);
    }, [pathname]);

    // State to track selected tab (provided by Home component via context)
    const [selectedHomeTab, setSelectedHomeTab] = React.useState<string | null>(
        null,
    );

    // Check if we're on the Automated Insights tab
    const isOnAutomatedInsightsTab = React.useMemo(() => {
        if (!isOnHomePage) return false;
        return selectedHomeTab === 'AI_INSIGHTS';
    }, [isOnHomePage, selectedHomeTab]);

    const selectedKeys = React.useMemo(() => {
        const option = menuOptions.find(option => {
            return (
                uniformPath(option.main.path) === fullPathname || // Explicit return for main path
                option.nested?.some(n => uniformPath(n.path) === fullPathname) // Explicit return for nested paths
            );
        });

        if (!option) return null; // Return null if no match is found

        if (uniformPath(option.main.path) === fullPathname) {
            return [option.main.title]; // Main path matches
        }

        const nested = option.nested?.find(
            n => uniformPath(n.path) === fullPathname,
        );
        if (nested) {
            return [option.main.title, nested.title]; // Both main and nested paths match
        }

        return [option.main.title]; // Fallback to main title only
    }, [menuOptions, fullPathname]);

    // Sync openKeys with selectedKeys to auto-open parent menu for nested items
    useEffect(() => {
        if (selectedKeys && selectedKeys.length > 1) {
            // If a nested item is selected, ensure its parent menu is open
            setOpenKeys([selectedKeys[0]]);
        }
        // If only a main item is selected, keep existing openKeys state
        // to preserve manually opened/closed submenus
    }, [selectedKeys]);

    useEffect(() => {
        if (isLoggedIn) {
            getFilters();
        }
    }, [client.id, isLoggedIn, rightsholderAttribution]);

    const renderMenuItems = (options: any[]) => {
        if (!options || options.length === 0 || showOnboardingOnly) {
            return [];
        }
        return options
            .map(option => {
                const { main, nested } = option;

                const menuItem: MenuItem = {
                    key: main.title,
                    icon: main.icon && (
                        <i className={`fa-regular ${main.icon}`} />
                    ),
                    label: main.title,
                    children: null,
                    onClick: () => main.path && navigate(main.path), // Add navigation on click
                    style: {
                        fontSize: 14,
                    },
                };

                if (nested && nested?.length > 0) {
                    menuItem.children = nested
                        .map((subOption: any) => {
                            const disabled =
                                subOption.permissions &&
                                subOption.permissions.some(
                                    p => !client.permissions?.includes(p),
                                );

                            if (disabled) {
                                return false;
                            }
                            return {
                                key: subOption.title,
                                label: subOption.title,
                                path: subOption.path,
                                style: {
                                    fontSize: 14,
                                },
                                onClick: () =>
                                    subOption.path && navigate(subOption.path), // Add navigation on click for nested items
                            };
                        })
                        .filter(Boolean); // Filter out any false values
                }

                if (
                    (!menuItem.children || menuItem.children.length === 0) &&
                    !main.path
                ) {
                    return null;
                }

                return menuItem;
            })
            .filter(Boolean);
    };

    const menuItems = React.useMemo(
        () => renderMenuItems(menuOptions),
        [menuOptions],
    );

    const breadcrumbs = React.useMemo(() => {
        const [path] = fullPathname.split('/').slice(3);
        const breadcrumbMap = getBreadcrumbMap(t);
        return breadcrumbMap[path] || [];
    }, [fullPathname, t]);

    const isOnReportsPage = breadcrumbs?.includes(t('breadcrumbs.reports'));

    // Determine if we should show automated insights note
    // This is true when on home page and on Automated Insights tab
    const isOnAutomatedInsightsPage = isOnHomePage && isOnAutomatedInsightsTab;

    const isOnSmpPage = breadcrumbs?.includes(
        t('breadcrumbs.socialMediaExposure'),
    );

    if (!isLoggedIn) {
        return (
            <Navigate
                to={{
                    pathname: '/login',
                }}
            />
        );
    }

    // Generate menu items from menuOptions

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider
                collapsible
                collapsed={collapsed}
                onCollapse={value => setCollapsed(value)}
                theme="light"
                width={272}
                trigger={null}
            >
                <Flex
                    className="app-container__sider"
                    style={{
                        maxHeight: '100vh',
                        minHeight: '100vh',
                        position: 'sticky',
                        ...(hasScrolled && { top: 0 }),
                        flexDirection: 'column',
                        background: colorBgLayout,
                    }}
                >
                    <Flex className="logo-wrapper">
                        <img
                            className="logo logo--circle"
                            src={relo_edge_circle}
                            style={{
                                opacity: collapsed && deboucedCollapsed ? 1 : 0,
                            }}
                        />
                        <img
                            className="logo logo--full"
                            src={relo_edge_full}
                            style={{
                                opacity:
                                    !collapsed && !deboucedCollapsed ? 1 : 0,
                            }}
                        />
                    </Flex>

                    <Menu
                        theme="light"
                        selectedKeys={selectedKeys || []}
                        openKeys={openKeys}
                        onOpenChange={keys => setOpenKeys(keys)}
                        mode="inline"
                        items={menuItems} // Use the dynamically generated menu items
                        style={{
                            background: colorBgLayout,
                            border: 'none',
                            flex: 1,
                            overflow: 'auto',
                        }}
                    />
                    {!collapsed && (
                        <span className="app-container__version">
                            {__VERSION__}
                        </span>
                    )}
                </Flex>
            </Sider>
            <Layout>
                <Header
                    style={{
                        padding: 0,
                        background: colorBgLayout,
                        flex: 1,
                        flexDirection: 'row',
                        maxHeight: 60,
                        ...(hasScrolled && { top: 0 }),
                        zIndex: 1,
                        width: '100%',
                        position: 'sticky',
                    }}
                >
                    <Flex
                        justify="space-between"
                        align="center"
                        style={{
                            width: '100%',
                            paddingRight: '24px',
                            maxHeight: '100%',
                            height: '60px',
                        }}
                    >
                        <Flex justify="center" align="center" gap={12}>
                            <Button
                                onClick={toggleMenu}
                                type="text"
                                style={{
                                    marginLeft: '4px',
                                    color: '#515252',
                                }}
                            >
                                {collapsed ? (
                                    <MenuUnfoldOutlined />
                                ) : (
                                    <MenuFoldOutlined />
                                )}
                            </Button>
                            {/* Use span instead of Typography.Text to avoid Ant Design ellipsis/ResizeObserver race (removeChild NotFoundError, SPORTS-MVC-6GB) */}
                            <Breadcrumb
                                style={{ margin: '16px 0' }}
                                items={breadcrumbs.map((breadcrumb, index) => ({
                                    key: `${breadcrumb}-${index}`,
                                    title: (
                                        <span style={{ fontSize: '14px' }}>
                                            {breadcrumb}
                                        </span>
                                    ),
                                }))}
                            />
                        </Flex>
                        <Flex align="center" gap={80}>
                            {showRhAttribution && (
                                <RhAttributionToggle
                                    isOnSmpPage={isOnSmpPage}
                                    isOnReportsPage={isOnReportsPage}
                                    isOnAutomatedInsightsPage={
                                        isOnAutomatedInsightsPage
                                    }
                                />
                            )}
                            <Flex align="center" gap={8}>
                                <ResourceCenter />
                                <AvatarMenu
                                    name={name}
                                    headerImgUrl={headerImgUrl}
                                    clients={clients}
                                    clientId={user.selectedClientId}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                </Header>
                <Layout
                    style={{
                        zIndex: 0,
                    }}
                >
                    <Content
                        style={{
                            margin: 0,
                            minHeight: 280,
                            background: colorBgContainer,
                        }}
                    >
                        <HomeTabContext.Provider
                            value={{
                                selectedTab: selectedHomeTab,
                                setSelectedTab: setSelectedHomeTab,
                            }}
                        >
                            <ContainerContext.Provider
                                value={{
                                    containerRef: containerRef.current,
                                }}
                            >
                                {children}
                            </ContainerContext.Provider>
                        </HomeTabContext.Provider>
                    </Content>
                </Layout>
            </Layout>
            {!agreed && <GdprConsent />}
            <ReportDownloading />
            {showAutomatedCollections && <CollectionsSnack />}
            {showAutomatedCampaigns && <CampaignSnack />}
        </Layout>
    );
};

AppContainer.propTypes = {
    children: PropTypes.node,
    getFilters: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    client: PropTypes.object.isRequired,
};

export default connect(['user', 'client'], userActions)(AppContainer);
